import React, { useCallback, useEffect, useState } from 'react';
import './App.css';
import Sidebar from './components/Sidebar/Sidebar';
import Intro from './components/Intro/Intro';
import Experience from './components/Experience/Experience';
import Projects from './components/Projects/Projects';
import Stack from './components/Stack/Stack';
import Credentials from './components/Credentials/Credentials';
import Resume from './components/Resume/Resume';
import Contact from './components/Contact/Contact';
import Footer from './components/Footer/Footer';
import Assistant from './components/Assistant/Assistant';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import { personalInfo } from './data/portfolioData';

const LEGAL = {
  '/privacy': { title: 'Privacy policy', Page: Privacy },
  '/terms': { title: 'Terms of service', Page: Terms }
};

const normalise = (pathname) => {
  const trimmed = pathname.replace(/\/+$/, '') || '/';
  return LEGAL[trimmed] ? trimmed : '/';
};

function App() {
  const [path, setPath] = useState(() => normalise(window.location.pathname));

  useEffect(() => {
    const onPop = () => setPath(normalise(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback(
    (target) => {
      // Section ids scroll; paths push history.
      if (!target.startsWith('/')) {
        if (path !== '/') {
          window.history.pushState({}, '', `/#${target}`);
          setPath('/');
          window.requestAnimationFrame(() => {
            document.getElementById(target)?.scrollIntoView({ block: 'start' });
          });
          return;
        }
        if (target === 'top') {
          window.scrollTo({ top: 0 });
          return;
        }
        document.getElementById(target)?.scrollIntoView({ block: 'start' });
        return;
      }

      const next = normalise(target);
      window.history.pushState({}, '', next);
      setPath(next);
      window.scrollTo({ top: 0 });
    },
    [path]
  );

  // Honour a deep link such as /#projects once the sections exist.
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id && path === '/') document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [path]);

  useEffect(() => {
    const entry = LEGAL[path];
    document.title = entry
      ? `${entry.title} | ${personalInfo.name}`
      : `${personalInfo.name}, Software Engineer`;
  }, [path]);

  const legal = LEGAL[path];

  return (
    <div className={legal ? 'app app-legal' : 'app'}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      {legal ? (
        <header className="legal-bar">
          <a
            className="legal-bar-name"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
          >
            {personalInfo.name}
          </a>
          <a
            className="link"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
          >
            back to the portfolio
          </a>
        </header>
      ) : (
        <Sidebar onNavigate={navigate} />
      )}

      <div className="main-col">
        <main id="main">
          {legal ? (
            <legal.Page />
          ) : (
            <>
              <Intro />
              <Experience />
              <Projects />
              <Stack />
              <Credentials />
              <Resume />
              <Contact />
            </>
          )}
        </main>
        <Footer onNavigate={navigate} />
      </div>

      {!legal && <Assistant />}
    </div>
  );
}

export default App;
